import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { fetchJson } from "@/lib/admin-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * A site's classification schedule: the badge, the enable / disable row and the enable dialog
 * (POS2-648, moved out of `pages/admin/databridge.tsx` by POS2-1383).
 *
 * `/admin/databridge` reaches the schedule through the old site and `/admin/sites` through
 * `site_id`, but both edit the same config row through the same service functions. So the caller
 * passes the classification endpoint and what to refresh after a change, and nothing else differs.
 */

export interface ClassificationJobCounts {
  pending: number;
  running: number;
  done: number;
  failed: number;
}

export interface ClassificationSourceCounts {
  ai: number;
  food_map: number;
  pending: number;
}

export interface ClassificationStatusEntry {
  enabled: boolean;
  last_run_id: string | null;
  open_ended: boolean;
  counts: ClassificationJobCounts;
  // POS2-699: present only on the single-site status endpoint, null on the
  // batched /statuses list.
  source_counts: ClassificationSourceCounts | null;
}

/** A device the dialog can scope a run to. */
export interface ScheduleDevice {
  serial: string;
  name: string | null;
}

interface ClassificationPreview {
  pending: number;
  already_classified: number;
  mapping_classified: number;
  protected: number;
}

/** What the dialog reads back from either page's PUT. */
interface ClassificationRun {
  last_run_id: string | null;
  counts: ClassificationJobCounts;
}

async function putClassification(
  url: string,
  headers: Record<string, string>,
  body: Record<string, unknown>,
): Promise<ClassificationRun> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(detail?.detail ?? `${res.status}: ${res.statusText}`);
  }
  const json = (await res.json()) as { data: ClassificationRun };
  return json.data;
}

export function ClassificationBadge({
  classification,
}: {
  classification: ClassificationStatusEntry;
}) {
  const { pending, running, done, failed } = classification.counts;
  const total = pending + running + done + failed;
  const draining = pending + running > 0;
  // POS2-699: open-ended configs never "finish" on their own — new events keep
  // topping up the run — so they stay "Classifying". Only a finite-scope config
  // with a drained queue is "Classified".
  const ongoing = draining || classification.open_ended;
  const title = draining
    ? `Classifying — ${done + failed}/${total} processed${classification.open_ended ? " — open-ended, keeps going as new events arrive" : ""}`
    : classification.open_ended
      ? "Classifying — open-ended; new events are classified automatically as they arrive"
      : `Classified — ${done} done${failed > 0 ? `, ${failed} failed` : ""}; won't resume without re-enabling`;
  return (
    <Badge
      variant="outline"
      title={title}
      className={
        ongoing
          ? "gap-1 border-blue-500/40 bg-blue-50 text-blue-700"
          : "gap-1 border-violet-500/40 bg-violet-50 text-violet-700"
      }
    >
      <Sparkles className="size-3" />
      {ongoing ? "Classifying" : "Classified"}
    </Badge>
  );
}

/**
 * Enable or disable the schedule. `enableBlockedReason` is why enabling is not possible yet
 * (DataBridge: no waste import), shown as a tooltip on a disabled button; null when it is.
 */
export function ClassificationToggleRow({
  classificationUrl,
  classification,
  enableBlockedReason,
  onConfigure,
  onChanged,
}: {
  classificationUrl: string;
  classification: ClassificationStatusEntry;
  enableBlockedReason: string | null;
  onConfigure: () => void;
  onChanged: () => void;
}) {
  const headers = useApiHeaders();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const disable = useMutation<ClassificationRun, Error, void>({
    mutationFn: () => putClassification(classificationUrl, headers, { enabled: false }),
    onMutate: () => setSubmitError(null),
    onError: (err) => setSubmitError(err.message),
    onSettled: onChanged,
  });

  const { pending, running, done, failed } = classification.counts;
  const total = pending + running + done + failed;
  const active = pending + running > 0;
  // "Disable" only means something while there is something to stop: an
  // open-ended config (standing top-ups) or a run still draining.
  // A finished bounded run reverts to "Enable" so the next range can be run.
  const showDisable = classification.enabled && (classification.open_ended || active);
  // Progress text only while there's a live run or a standing open-ended
  // config — a finished bounded run reverts to the idle hint.
  const subLabel = showDisable
    ? total > 0
      ? `${done + failed}/${total} processed${failed > 0 ? ` — ${failed} failed` : ""}${
          classification.open_ended ? " — open-ended" : ""
        }`
      : "No candidate events yet"
    : "AI-classify the site's waste events";

  return (
    <div className="space-y-1">
      {enableBlockedReason === null ? (
        <Button
          variant={showDisable ? "outline" : "default"}
          className="w-full"
          onClick={() => (showDisable ? disable.mutate() : onConfigure())}
          disabled={disable.isPending}
        >
          {disable.isPending
            ? "Saving…"
            : showDisable
              ? "Disable Classification"
              : "Enable Classification"}
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-full">
              <Button variant="default" className="w-full" disabled>
                Enable Classification
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{enableBlockedReason}</TooltipContent>
        </Tooltip>
      )}
      <p className="text-[10px] text-muted-foreground">{subLabel}</p>
      {submitError && <p className="text-[10px] text-destructive">{submitError}</p>}
    </div>
  );
}

export function EnableClassificationDialog({
  open,
  onOpenChange,
  classificationUrl,
  siteName,
  devices,
  watchCopyingOldLabels,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  classificationUrl: string;
  siteName: string | null;
  devices: ScheduleDevice[];
  watchCopyingOldLabels: boolean;
  onChanged: () => void;
}) {
  const headers = useApiHeaders();
  const [scope, setScope] = useState<"all" | "specific">("all");
  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reclassify, setReclassify] = useState(false);
  const [reclassifyMapped, setReclassifyMapped] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Date inputs are whole days; events are timestamped. Expand to full-day
  // UTC bounds so the end date is inclusive.
  const startIso = startDate ? `${startDate}T00:00:00Z` : null;
  const endIso = endDate ? `${endDate}T23:59:59Z` : null;
  const deviceSerials = scope === "specific" ? selectedSerials : null;
  const scopeReady = scope === "all" || selectedSerials.length > 0;

  const previewParams = new URLSearchParams();
  deviceSerials?.forEach((s) => previewParams.append("device_serials", s));
  if (startIso) previewParams.set("start_date", startIso);
  if (endIso) previewParams.set("end_date", endIso);

  const previewQuery = useQuery<ClassificationPreview, Error>({
    queryKey: [
      "admin",
      "classification-preview",
      classificationUrl,
      scope,
      selectedSerials,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchJson<ClassificationPreview>(`${classificationUrl}/preview?${previewParams}`, headers),
    enabled: open && scopeReady,
    retry: false,
  });

  const enableMutation = useMutation<ClassificationRun, Error, void>({
    mutationFn: () =>
      putClassification(classificationUrl, headers, {
        enabled: true,
        device_serials: deviceSerials,
        start_date: startIso,
        end_date: endIso,
        reclassify,
        reclassify_mapped: reclassifyMapped,
      }),
    onSuccess: onChanged,
    onError: (err) => setSubmitError(err.message),
  });

  const resetMutation = enableMutation.reset;
  useEffect(() => {
    if (!open) {
      setScope("all");
      setSelectedSerials([]);
      setStartDate("");
      setEndDate("");
      setReclassify(false);
      setReclassifyMapped(false);
      setSubmitError(null);
      resetMutation();
    }
  }, [open, resetMutation]);

  const preview = previewQuery.data;
  const result = enableMutation.data;
  const candidates = preview
    ? preview.pending +
      (reclassify ? preview.already_classified : 0) +
      (reclassifyMapped ? preview.mapping_classified : 0)
    : 0;
  // POS2-699: an open-ended config (no end date) is forward-looking — new
  // events are classified as they arrive — so enabling with 0 candidates is
  // valid. A closed range with 0 candidates would never classify anything, so
  // it stays disabled.
  const openEnded = !endDate;
  // POS2-699: the symmetric guard to the watch-mode one — an open-ended run
  // auto-classifies every watch-synced event, which would race watch's
  // copy-old-labels pass. Block it while watch is copying; a finite run is safe
  // (it doesn't enqueue watch events), so an end date clears the block.
  const wouldRaceWatchCopy = watchCopyingOldLabels && openEnded;
  // POS2-960: the preview counts scan the whole site and time out on large sites,
  // but the mutation never needed them. Keep Enable clickable while counts load or
  // fail; only enforce the "closed range with 0 candidates" guard when counts are known.
  const submitDisabled =
    !scopeReady ||
    enableMutation.isPending ||
    wouldRaceWatchCopy ||
    (!!preview && candidates === 0 && !openEnded);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enable Classification</DialogTitle>
          <DialogDescription>
            {siteName ?? "Select a site"} → run AI waste classification over the site's events. Runs
            in the background, one device at a time.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4 py-2 text-sm">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Device scope</p>
              <Select value={scope} onValueChange={(v) => setScope(v as "all" | "specific")}>
                <SelectTrigger className="w-full" aria-label="Device scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All site devices</SelectItem>
                  <SelectItem value="specific">Specific devices</SelectItem>
                </SelectContent>
              </Select>
              {scope === "specific" && (
                <div className="max-h-32 space-y-1 overflow-y-auto rounded border p-2">
                  {devices.map((d) => (
                    <label key={d.serial} className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={selectedSerials.includes(d.serial)}
                        onCheckedChange={(checked) =>
                          setSelectedSerials((prev) =>
                            checked ? [...prev, d.serial] : prev.filter((s) => s !== d.serial),
                          )
                        }
                      />
                      <span className="font-mono">{d.serial}</span>
                      <span className="text-muted-foreground">{d.name ?? ""}</span>
                    </label>
                  ))}
                  {devices.length === 0 && (
                    <p className="text-xs text-muted-foreground">No devices for this site.</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Start date (optional)</p>
                <Input
                  type="date"
                  aria-label="Start date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">End date (optional)</p>
                <Input
                  type="date"
                  aria-label="End date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            {wouldRaceWatchCopy && (
              <p className="text-xs text-amber-700">
                Watch mode is copying old classifications for this site, so an open-ended AI run
                would re-classify the same synced events and conflict. Set an end date for a one-off
                run, or turn off "copy old classifications" in watch mode first.
              </p>
            )}
            {!endDate && !wouldRaceWatchCopy && (
              <p className="text-xs text-muted-foreground">
                {preview && candidates === 0
                  ? "No events to classify yet — enabling sets up a forward-looking config; new events are classified automatically as they arrive."
                  : "No end date — classifies everything in scope now; events that arrive later are classified automatically."}
              </p>
            )}

            <label htmlFor="classification-reclassify" className="flex items-center gap-2 text-xs">
              <Checkbox
                id="classification-reclassify"
                checked={reclassify}
                onCheckedChange={(checked) => setReclassify(checked === true)}
              />
              Reclassify already-classified events
            </label>

            <label
              htmlFor="classification-reclassify-mapped"
              className="flex items-center gap-2 text-xs"
            >
              <Checkbox
                id="classification-reclassify-mapped"
                checked={reclassifyMapped}
                onCheckedChange={(checked) => setReclassifyMapped(checked === true)}
              />
              Reclassify mapping-classified events (from old labels)
            </label>

            {previewQuery.isLoading && scopeReady && <Skeleton className="h-16 w-full" />}
            {previewQuery.isError && (
              <p className="text-xs text-muted-foreground">
                Counts unavailable (large site) — you can still enable classification.
              </p>
            )}
            {preview && (
              <div className="rounded border bg-muted/30 p-3 text-xs space-y-2">
                <div className="flex justify-between">
                  <span>Unclassified events in scope</span>
                  <span className="font-medium">{preview.pending}</span>
                </div>
                <div
                  className={`flex justify-between ${reclassify ? "" : "text-muted-foreground"}`}
                >
                  <span>Already classified{reclassify ? " (will reclassify)" : " (skipped)"}</span>
                  <span>{preview.already_classified}</span>
                </div>
                <div
                  className={`flex justify-between ${reclassifyMapped ? "" : "text-muted-foreground"}`}
                >
                  <span>
                    Mapped from old labels
                    {reclassifyMapped ? " (will reclassify)" : " (skipped)"}
                  </span>
                  <span>{preview.mapping_classified}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Confirmed / manual (always skipped)</span>
                  <span>{preview.protected}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">To classify this run</span>
                  <span className="font-medium">{candidates}</span>
                </div>
              </div>
            )}

            {submitError && <p className="text-xs font-mono text-destructive">{submitError}</p>}
          </div>
        )}

        {result && (
          <div className="rounded border bg-emerald-50 p-3 text-xs text-emerald-900 space-y-1">
            <p className="font-medium">Classification scheduled</p>
            <p>
              {result.counts.pending === 0
                ? "No events to classify right now — new events will be classified automatically as they arrive."
                : `${result.counts.pending} event${result.counts.pending === 1 ? "" : "s"} queued. The worker processes them in the background; the status badge updates as jobs complete.`}
            </p>
            {result.last_run_id && (
              <p className="font-mono text-emerald-700">run_id {result.last_run_id.slice(0, 8)}…</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={() => enableMutation.mutate()} disabled={submitDisabled}>
              {enableMutation.isPending ? "Scheduling…" : "Enable classification"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
